/**
 * Her goals, and what the creature is called.
 */
import { useEffect, useState } from "react";
import { api, type Me, type Settings as SettingsData } from "../lib/api";

type Macro = "protein" | "carbs" | "fat";

/** The macros, and how many calories each gram of them holds. */
const MACROS: { macro: Macro; label: string; key: keyof SettingsData; kcalPerGram: number }[] = [
  { macro: "protein", label: "Protein", key: "goal_protein_g", kcalPerGram: 4 },
  { macro: "carbs", label: "Carbs", key: "goal_carbs_g", kcalPerGram: 4 },
  { macro: "fat", label: "Fat", key: "goal_fat_g", kcalPerGram: 9 },
];

/** Tidies a percentage to at most one decimal place. */
const tidy = (value: number) => String(Math.round(value * 10) / 10);

/**
 * The percentages are worked out from the gram goals that are saved, and turned
 * back into grams on saving, so the rest of the app only ever deals in grams.
 */
function percentsFrom(settings: SettingsData): Record<Macro, string> {
  const percent = (grams: number, kcalPerGram: number) =>
    settings.goal_calories > 0 ? tidy((grams * kcalPerGram * 100) / settings.goal_calories) : "0";
  return {
    protein: percent(settings.goal_protein_g, 4),
    carbs: percent(settings.goal_carbs_g, 4),
    fat: percent(settings.goal_fat_g, 9),
  };
}

export function Settings() {
  const [settings, setSettings] = useState<SettingsData | null>(null);
  // Kept as typed, so a half-typed "32." isn't tidied away mid-keystroke.
  const [percents, setPercents] = useState<Record<Macro, string>>({ protein: "", carbs: "", fat: "" });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);

  // Only so the sign-out card can say which account she is in as. Its own
  // request, because failing to find out must not stop the settings loading.
  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    api
      .settings()
      .then((loaded) => {
        setSettings(loaded);
        setPercents(percentsFrom(loaded));
      })
      .catch((problem) => setError(problem.message));
  }, []);

  if (error) return <div className="notice notice--bad">{error}</div>;
  if (!settings) return <div className="empty">Loading…</div>;

  const set = (patch: Partial<SettingsData>) => {
    setSettings({ ...settings, ...patch });
    setSaved(false);
  };

  async function save() {
    try {
      const grams = Object.fromEntries(
        MACROS.map(({ macro, key, kcalPerGram }) => [key, gramsFor(macro, kcalPerGram)]),
      );
      setSettings(await api.saveSettings({ ...settings!, ...grams }));
      setSaved(true);
    } catch (problem) {
      setError((problem as Error).message);
    }
  }

  /** The grams a macro's percentage comes to, at the current calorie goal. */
  function gramsFor(macro: Macro, kcalPerGram: number): number {
    return (settings!.goal_calories * ((Number(percents[macro]) || 0) / 100)) / kcalPerGram;
  }

  const totalPercent = MACROS.reduce((sum, { macro }) => sum + (Number(percents[macro]) || 0), 0);

  return (
    <>
      <h1 className="screen-title">Settings</h1>

      <div className="card">
        <label className="field">
          <span>Your creature's name</span>
          <input
            className="input"
            value={settings.creature_name}
            onChange={(event) => set({ creature_name: event.target.value })}
          />
        </label>
      </div>

      <div className="card">
        <h2 style={{ fontSize: "1rem", marginBottom: 12 }}>Daily goals</h2>
        <label className="field">
          <span>Calories</span>
          <input
            className="input"
            inputMode="numeric"
            value={String(settings.goal_calories)}
            onChange={(event) => set({ goal_calories: Number(event.target.value) || 0 })}
          />
        </label>

        <div className="macro-goals">
          {MACROS.map(({ macro, label, kcalPerGram }) => (
            <label className="field" key={macro}>
              <span>{label} %</span>
              <input
                className="input"
                inputMode="decimal"
                value={percents[macro]}
                onChange={(event) => {
                  setPercents({ ...percents, [macro]: event.target.value });
                  setSaved(false);
                }}
              />
              <span className="muted small">{Math.round(gramsFor(macro, kcalPerGram))} g a day</span>
            </label>
          ))}
        </div>

        <p className={`small macro-total${Math.round(totalPercent) === 100 ? " muted" : " macro-total--off"}`}>
          {Math.round(totalPercent) === 100
            ? "These add up to 100% of your calories."
            : `These add up to ${tidy(totalPercent)}% of your calories, not 100%.`}
        </p>

        <button className="button button--full" onClick={() => void save()}>
          {saved ? "Saved ✓" : "Save"}
        </button>
      </div>

      <div className="card">
        <p className="muted small" style={{ margin: 0 }}>
          To put this app on your home screen: open it in Safari, tap the share
          button, then "Add to Home Screen". It will open like a normal app and
          work without a signal — though adding things does need one.
        </p>
      </div>

      {me?.signedIn && (
        <div className="card">
          <p className="muted small" style={{ marginTop: 0 }}>
            Signed in as {me.email}
            {me.dev && " (a pretend account, because this is running on your own computer)"}.
          </p>
          {/* A form rather than a button that calls the server quietly: signing
              out should leave the page as well as the cookie, and letting the
              browser do the whole thing means there is no half-signed-out
              state to get stuck in. */}
          <form method="post" action="/api/auth/signout">
            <button type="submit" className="button button--quiet button--full">
              Sign out
            </button>
          </form>
        </div>
      )}
    </>
  );
}
